import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useSiteInfo } from '@/SiteInfo/hooks/use-site-info';

interface PrivacyPolicySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PrivacyPolicySheet = ({ open, onOpenChange }: PrivacyPolicySheetProps) => {
  const { data: siteInfo } = useSiteInfo();
  const siteName = siteInfo?.SiteTitle ?? 'Store';
  const hasCoupons = siteInfo?.SiteHasCoupons ?? false;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b text-left">
          <SheetTitle>Privacy Policy</SheetTitle>
          <SheetDescription>
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Introduction */}
          <section>
            <h3 className="text-lg font-semibold mb-3">1. Introduction</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {siteName} ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains
              how we collect, use, disclose, and safeguard your information when you visit our website, use our mobile
              application, or interact with our services. Please read this policy carefully to understand our practices
              regarding your personal information.
            </p>
          </section>

          <Separator />

          {/* Information We Collect */}
          <section>
            <h3 className="text-lg font-semibold mb-3">2. Information We Collect</h3>

            <h4 className="text-base font-medium mb-2 mt-4">Personal Information</h4>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              We may collect personal information that you voluntarily provide to us, including:
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
              {hasCoupons && (
                <>
                  <li><strong>Contact Information:</strong> Phone number (required for account creation), email address, name</li>
                  <li><strong>Profile Information:</strong> Birthday, ZIP code, communication preferences</li>
                  <li><strong>Loyalty Card Number:</strong> If you have a physical loyalty card linked to your account</li>
                </>
              )}
              <li><strong>Contact Form Submissions:</strong> Name, email address, phone number, and message content when you contact us</li>
              <li><strong>Location Preferences:</strong> Your preferred store location</li>
            </ul>

            <h4 className="text-base font-medium mb-2 mt-4">Automatically Collected Information</h4>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              When you access our Services, we automatically collect certain information:
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
              <li><strong>Device Information:</strong> Device type, operating system, browser type</li>
              <li><strong>Usage Data:</strong> Pages viewed, recipes accessed, ads viewed, features used</li>
              <li><strong>Analytics Data:</strong> We use Google Analytics to understand how visitors interact with our website</li>
              <li><strong>IP Address:</strong> For general location and security purposes</li>
              {hasCoupons && (
                <li><strong>Coupon Activity:</strong> Coupons clipped, redeemed, and viewed</li>
              )}
            </ul>
          </section>

          <Separator />

          {/* How We Use Your Information */}
          <section>
            <h3 className="text-lg font-semibold mb-3">3. How We Use Your Information</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              We use the information we collect to:
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
              <li>Provide, maintain, and improve our Services</li>
              <li>Process and fulfill your requests</li>
              {hasCoupons && (
                <>
                  <li>Manage your account and digital coupon activity</li>
                  <li>Send you verification codes via SMS for account authentication</li>
                  <li>Personalize your coupon and offer recommendations</li>
                </>
              )}
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Analyze usage patterns to improve user experience</li>
              <li>Detect and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <Separator />

          {/* SMS Communications */}
          {hasCoupons && (
            <>
              <section>
                <h3 className="text-lg font-semibold mb-3">4. SMS Communications</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  By providing your phone number and creating an account, you consent to receive SMS messages from us,
                  including:
                </p>
                <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
                  <li>Account verification codes</li>
                  <li>Promotional offers and deals (if opted in)</li>
                  <li>Important account notifications</li>
                </ul>
                <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                  <strong>Message frequency varies. Message and data rates may apply.</strong> You can opt out of
                  promotional SMS messages at any time by updating your account settings or replying STOP to any
                  promotional message. Note that opting out of promotional messages will not affect transactional
                  messages such as verification codes.
                </p>
              </section>

              <Separator />
            </>
          )}

          {/* Cookies and Tracking */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '5' : '4'}. Cookies and Tracking Technologies</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              We use cookies and similar tracking technologies to:
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
              <li>Remember your preferences and settings</li>
              <li>Maintain your session while browsing</li>
              <li>Analyze site traffic and usage patterns</li>
              <li>Deliver relevant content and advertisements</li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed mt-3">
              You can control cookies through your browser settings. However, disabling cookies may affect the
              functionality of certain features.
            </p>
          </section>

          <Separator />

          {/* Information Sharing */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '6' : '5'}. Information Sharing</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              We may share your information with:
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
              <li><strong>Service Providers:</strong> Companies that help us operate our Services (e.g., analytics providers, email services, SMS providers)</li>
              {hasCoupons && (
                <li><strong>Loyalty Program Partners:</strong> To process and validate coupon redemptions</li>
              )}
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed mt-3">
              <strong>We do not sell your personal information to third parties.</strong>
            </p>
          </section>

          <Separator />

          {/* Data Retention */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '7' : '6'}. Data Retention</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this
              Privacy Policy, unless a longer retention period is required or permitted by law. When you delete your
              account, we will delete or anonymize your personal information within 30 days, except as required for
              legal compliance or legitimate business purposes.
            </p>
          </section>

          <Separator />

          {/* Your Rights */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '8' : '7'}. Your Rights and Choices</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              Depending on your location, you may have the following rights:
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Opt-Out:</strong> Opt out of promotional communications</li>
              {hasCoupons && (
                <li><strong>Account Deletion:</strong> Delete your account through the account settings</li>
              )}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed mt-3">
              To exercise these rights, please contact us through our Contact page or visit a store location.
            </p>
          </section>

          <Separator />

          {/* Children's Privacy */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '9' : '8'}. Children's Privacy</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Our Services are not intended for children under 13 years of age. We do not knowingly collect personal
              information from children under 13. If you are a parent or guardian and believe we have collected
              information from your child, please contact us immediately so we can delete the information.
            </p>
          </section>

          <Separator />

          {/* Security */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '10' : '9'}. Security</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information
              against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission
              over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <Separator />

          {/* Changes to Policy */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '11' : '10'}. Changes to This Policy</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by
              posting the new Privacy Policy on our website and updating the "Last updated" date. We encourage you
              to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <Separator />

          {/* Contact */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '12' : '11'}. Contact Us</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us
              through our Contact page or visit one of our store locations. We will respond to your inquiry
              within a reasonable timeframe.
            </p>
          </section>

          {/* California Residents */}
          <Separator />

          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '13' : '12'}. California Privacy Rights</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              If you are a California resident, you have additional rights under the California Consumer Privacy
              Act (CCPA), as amended by the California Privacy Rights Act (CPRA):
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
              <li><strong>Right to Know:</strong> Request the categories and specific pieces of personal information we collect, use, disclose, or share</li>
              <li><strong>Right to Delete:</strong> Request deletion of the personal information we hold about you</li>
              <li><strong>Right to Opt-Out:</strong> Opt out of the sale or sharing of your personal information</li>
              <li><strong>Right to Non-Discrimination:</strong> Not be discriminated against for exercising your privacy rights</li>
              <li><strong>Right to Limit:</strong> Limit the use and disclosure of your sensitive personal information</li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed mt-3">
              <strong>We do not sell your personal information.</strong> To exercise your right to opt out of the sale
              or sharing of personal information, use the "Your Privacy Choices" link in the
              footer of this site. We also honor the Global Privacy Control (GPC) signal: if your browser sends a GPC
              signal, we treat it as a request to opt out of the sale or sharing of your personal information.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mt-3">
              To exercise any of your other California privacy rights, please contact us through our Contact page.
            </p>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};
